import { randomString } from '@/composables/helperFunctions'

export class CompiledChartTimeSetup {
  start: number
  end: number
  timeResolution: number

  constructor(start: number, end: number, timeResolution: number) {
    this.start = start
    this.end = end
    this.timeResolution = timeResolution
  }
}

export type SerializedGeneralChartTimeSetup = {
  timeResolution: number | null
  absoluteStart: number | null
  absoluteEnd: number | null
  relativeStart: number | null
  relativeEnd: number | null
  startRelativeTo: 'now' | 'end' | null
  endRelativeTo: 'now' | 'start' | null
  synchronizationKey: string | null
  synchronized: boolean
  fixedTimeResolution: number | null
}
const quantizedTimeResolutions = [
  1000,
  1000 * 10,
  1000 * 30,
  1000 * 60,
  1000 * 60 * 2,
  1000 * 60 * 5,
  1000 * 60 * 10,
  1000 * 60 * 15,
  1000 * 60 * 20,
  1000 * 60 * 30,
  1000 * 60 * 60,
  1000 * 60 * 60 * 2,
  1000 * 60 * 60 * 3,
  1000 * 60 * 60 * 4,
  1000 * 60 * 60 * 6,
  1000 * 60 * 60 * 8,
  1000 * 60 * 60 * 12,
  1000 * 60 * 60 * 24,
  1000 * 60 * 60 * 24 * 2,
  1000 * 60 * 60 * 24 * 3,
  1000 * 60 * 60 * 24 * 4,
  1000 * 60 * 60 * 24 * 7,
  1000 * 60 * 60 * 24 * 7 * 2,
  1000 * 60 * 60 * 24 * 7 * 4,
  1000 * 60 * 60 * 24 * 7 * 12,
  1000 * 60 * 60 * 24 * 7 * 24,
  1000 * 60 * 60 * 24 * 7 * 52,
]

function quantizeToSeconds(timestamp: number): number {
  return timestamp - (timestamp % 1000)
}
export function quantizeTimeResolution(start: number, end: number): number {
  const targetDataPoints = 250
  const targetResolution = Math.ceil((end - start) / targetDataPoints)
  return quantizedTimeResolutions.reduce((prev, curr) => (Math.abs(curr - targetResolution) < Math.abs(prev - targetResolution) ? curr : prev))
}

export class GeneralChartTimeSetup {
  get id(): string {
    return this._id
  }
  get synchronizationKey(): string | null {
    return this._synchronizationKey
  }
  private now: number
  timeResolution: number | null
  fixedTimeResolution: number | null
  absoluteStart: number | null
  absoluteEnd: number | null
  relativeStart: number | null
  relativeEnd: number | null
  startRelativeTo: 'now' | 'end' | null
  endRelativeTo: 'now' | 'start' | null
  private readonly _id: string
  private readonly _synchronizationKey: string | null
  private synchronized: boolean = false
  private updateListeners: Array<(compiled: CompiledChartTimeSetup) => void> = []

  constructor(
    timeResolution: number | null,
    absoluteStart: number | null,
    absoluteEnd: number | null,
    relativeStart: number | null,
    relativeEnd: number | null,
    startRelativeTo: 'now' | 'end' | null,
    endRelativeTo: 'now' | 'start' | null,
    synchronizationKey: string | null = null,
    synchronized: boolean = false,
    fixedTimeResolution: number | null = null
  ) {
    this.now = Date.now()
    this._id = randomString(8)
    this.timeResolution = timeResolution
    this.absoluteStart = absoluteStart
    this.absoluteEnd = absoluteEnd
    this.relativeStart = relativeStart
    this.relativeEnd = relativeEnd
    this.startRelativeTo = startRelativeTo
    this.endRelativeTo = endRelativeTo
    this._synchronizationKey = synchronizationKey
    this.synchronized = synchronized
    this.fixedTimeResolution = fixedTimeResolution
    timeSetupRegistry.addTimeSetup(this)
  }

  static fromSerialized(serialization: SerializedGeneralChartTimeSetup): GeneralChartTimeSetup {
    return new GeneralChartTimeSetup(
      serialization.timeResolution,
      serialization.absoluteStart,
      serialization.absoluteEnd,
      serialization.relativeStart,
      serialization.relativeEnd,
      serialization.startRelativeTo,
      serialization.endRelativeTo,
      serialization.synchronizationKey,
      serialization.synchronized,
      serialization.fixedTimeResolution
    )
  }
  static default(synchronizationKey: string | null = null, fixedTimeResolution: number | null = null): GeneralChartTimeSetup {
    return GeneralChartTimeSetup.lastWeek(synchronizationKey, fixedTimeResolution)
  }
  static lastWeek(synchronizationKey: string | null = null, fixedTimeResolution: number | null = null): GeneralChartTimeSetup {
    return GeneralChartTimeSetup.lastNDays(7, synchronizationKey, fixedTimeResolution)
  }
  static last3Days(synchronizationKey: string | null = null, fixedTimeResolution: number | null = null): GeneralChartTimeSetup {
    return GeneralChartTimeSetup.lastNDays(3, synchronizationKey, fixedTimeResolution)
  }
  static lastNDays(days: number, synchronizationKey: string | null = null, fixedTimeResolution: number | null = null) {
    return new GeneralChartTimeSetup(
      null,
      null,
      null,
      1000 * 60 * 60 * 24 * days,
      0,
      'end',
      'now',
      synchronizationKey,
      synchronizationKey !== null,
      fixedTimeResolution
    )
  }

  attachUpdateListener(listener: (compiled: CompiledChartTimeSetup) => void) {
    this.updateListeners.push(listener)
  }

  detachUpdateListener(listener: (compiled: CompiledChartTimeSetup) => void) {
    this.updateListeners.splice(this.updateListeners.indexOf(listener), 1)
  }

  serialize(): SerializedGeneralChartTimeSetup {
    return {
      timeResolution: this.timeResolution,
      absoluteStart: this.absoluteStart,
      absoluteEnd: this.absoluteEnd,
      relativeStart: this.relativeStart,
      relativeEnd: this.relativeEnd,
      startRelativeTo: this.startRelativeTo,
      endRelativeTo: this.endRelativeTo,
      synchronizationKey: this.synchronizationKey,
      synchronized: this.synchronized,
      fixedTimeResolution: this.fixedTimeResolution,
    }
  }

  compile(): CompiledChartTimeSetup {
    const result: CompiledChartTimeSetup = {
      end: this.getComputedEnd(),
      start: this.getComputedStart(),
      timeResolution: 0,
    }
    if (this.fixedTimeResolution) {
      result.timeResolution = this.fixedTimeResolution
    } else {
      if (this.timeResolution === null) {
        result.timeResolution = quantizeTimeResolution(result.start, result.end)
      } else result.timeResolution = this.timeResolution as number
    }
    return result
  }

  isSynchronized(): boolean {
    return this.synchronized
  }

  setSynchronized(synchronized: boolean): void {
    this.synchronized = synchronized
  }

  hasDefinedTimeResolution(): boolean {
    return this.timeResolution !== null
  }

  getComputedTimeResolution(): number {
    if (this.fixedTimeResolution) return this.fixedTimeResolution
    if (this.timeResolution !== null) return this.timeResolution
    return quantizeTimeResolution(this.getComputedStart(), this.getComputedEnd())
  }

  getAutoInferredTimeResolution(): number {
    return quantizeTimeResolution(this.getComputedStart(), this.getComputedEnd())
  }
  getComputedStart(): number {
    if (this.startRelativeTo === 'end' && this.endRelativeTo === 'start') throw new Error('Cannot compute start')
    if (this.absoluteStart !== null) {
      return quantizeToSeconds(this.absoluteStart)
    }
    if (this.startRelativeTo === 'now') return quantizeToSeconds(this.now - (this.relativeStart as number))
    return quantizeToSeconds(this.getComputedEnd() - (this.relativeStart as number))
  }

  getComputedEnd(): number {
    if (this.startRelativeTo === 'end' && this.endRelativeTo === 'start') throw new Error('Cannot compute end')
    if (this.absoluteEnd !== null) {
      return quantizeToSeconds(this.absoluteEnd)
    }
    if (this.endRelativeTo === 'now') return quantizeToSeconds(this.now - (this.relativeEnd as number))
    return quantizeToSeconds(this.getComputedStart() + (this.relativeEnd as number))
  }

  updateSetup(start: number, end: number, timeResolution: number | null, synchronized: boolean): void {
    this.absoluteStart = start
    this.absoluteEnd = end
    this.timeResolution = timeResolution
    this.synchronized = synchronized
    const compiled = this.compile()
    timeSetupRegistry.updateSynchronizedTimeSetups(this)
    this.updateListeners.forEach(listener => listener(compiled))
  }

  synchronizeSetup(start: number, end: number, timeResolution: number | null | undefined) {
    this.absoluteStart = start
    this.absoluteEnd = end
    if (timeResolution !== undefined) {
      this.timeResolution = timeResolution
    }
    const compiled = this.compile()
    this.updateListeners.forEach(listener => listener(compiled))
  }
}

class TimeSetupRegistry {
  private readonly _timeSetups: { [key: string]: GeneralChartTimeSetup } = {}

  constructor() {
    this._timeSetups = {}
  }

  addTimeSetup(timeSetup: GeneralChartTimeSetup): void {
    this._timeSetups[timeSetup.id] = timeSetup
  }

  getTimeSetup(id: string): GeneralChartTimeSetup | undefined {
    return this._timeSetups[id]
  }

  removeTimeSetup(id: string): void {
    delete this._timeSetups[id]
  }

  getTimeSetups(): GeneralChartTimeSetup[] {
    return Object.values(this._timeSetups)
  }

  updateSynchronizedTimeSetups(sourceTimeSetup: GeneralChartTimeSetup) {
    if (!sourceTimeSetup.isSynchronized()) return
    Object.values(this._timeSetups).forEach(timeSetup => {
      if (timeSetup.synchronizationKey === sourceTimeSetup.synchronizationKey && timeSetup.isSynchronized() && timeSetup.id !== sourceTimeSetup.id) {
        timeSetup.synchronizeSetup(sourceTimeSetup.getComputedStart(), sourceTimeSetup.getComputedEnd(), sourceTimeSetup.getComputedTimeResolution())
      }
    })
  }

  updateSynchronizedTimeSetupsWithAbsoluteTime(synchronizationKey: string, absoluteStart?: number, absoluteEnd?: number) {
    Object.values(this._timeSetups).forEach(timeSetup => {
      if (timeSetup.synchronizationKey === synchronizationKey && timeSetup.isSynchronized()) {
        const newStart = absoluteStart !== undefined ? absoluteStart : timeSetup.getComputedStart()
        const newEnd = absoluteEnd !== undefined ? absoluteEnd : timeSetup.getComputedEnd()
        timeSetup.synchronizeSetup(newStart, newEnd, null)
      }
    })
  }
}

export const timeSetupRegistry = new TimeSetupRegistry()
